import { Link } from "react-router-dom";
import { ShieldCheck, Users, MapPinned, Award, ArrowRight, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/layout/Breadcrumbs";
import CtaBand from "@/components/sections/CtaBand";
import { SectionHeading } from "@/components/ui-bits";
import { useSite, telLink } from "@/context/SiteContext";

const JODHPUR = "https://images.unsplash.com/photo-1602643454724-21d5a40722db?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const values = [
  { icon: ShieldCheck, title: "Safety First", desc: "Verified drivers, sanitised cars and careful highway driving on every trip." },
  { icon: Users, title: "Customer Focused", desc: "Friendly, flexible service tailored to your travel plans and comfort." },
  { icon: MapPinned, title: "Local Knowledge", desc: "Deep familiarity with every route, fort and hidden gem in Rajasthan." },
  { icon: Award, title: "Honest Pricing", desc: "Fair, transparent fares with no hidden charges or last-minute surprises." },
];

export default function About() {
  const s = useSite();
  return (
    <>
      <Seo title="About Us | Chouhan Taxi Service — Jodhpur Taxi & Tour Company"
        description="Learn about Chouhan Taxi Service, a Jodhpur-based taxi, cab and tour & travel company serving Jodhpur, Jaipur, Udaipur and Jaisalmer with reliable, comfortable travel."
        path="/about" />
      <Breadcrumbs items={[{ label: "About Us" }]} />
      <section className="section-pad">
        <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase font-bold tracking-[0.2em] text-brand mb-3">About Chouhan Taxi Service</div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-extrabold text-navy-heading leading-tight">Your Trusted Travel Partner in Rajasthan</h1>
            <p className="mt-5 text-slate-600 leading-relaxed">Chouhan Taxi Service is a locally rooted taxi, cab and tour & travel company based on Mehrangarh Fort Road in the heart of Jodhpur. We help travellers explore the Blue City and the wider state of Rajasthan with comfortable vehicles, professional drivers and honest, transparent fares.</p>
            <p className="mt-4 text-slate-600 leading-relaxed">Whether you need a quick local ride, a reliable airport transfer, a one-way outstation cab or a fully planned multi-day Rajasthan tour, our team is committed to making your journey safe, smooth and memorable. We serve all four of Rajasthan's most-loved destinations — Jodhpur, Jaipur, Udaipur and Jaisalmer — as well as popular routes to neighbouring cities like Ahmedabad and Delhi.</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/book-taxi" className="inline-flex items-center gap-2 bg-brand text-white font-semibold px-6 py-3 rounded-full hover:bg-royal transition-colors">Book a Taxi <ArrowRight className="w-4 h-4" /></Link>
              <a href={telLink(s)} className="inline-flex items-center gap-2 border border-slate-300 text-navy font-semibold px-6 py-3 rounded-full hover:border-brand transition-colors"><Phone className="w-4 h-4" /> {s.phone}</a>
            </div>
          </div>
          <div className="relative">
            <img src={JODHPUR} alt="Jodhpur Blue City and Mehrangarh Fort" className="rounded-3xl shadow-xl w-full h-[420px] object-cover" />
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-lg p-5 border border-slate-100 hidden sm:block">
              <div className="text-3xl font-heading font-extrabold text-brand">24/7</div>
              <div className="text-sm text-slate-500">Booking & Support</div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad bg-slate-50">
        <div className="container-x">
          <SectionHeading center eyebrow="Our Values" title="What Drives Us" subtitle="The principles behind every ride we provide." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => (
              <div key={v.title} className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm text-center card-hover">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-brand-light flex items-center justify-center mb-4"><v.icon className="w-7 h-7 text-brand" /></div>
                <h3 className="font-heading font-semibold text-navy-heading text-lg mb-2">{v.title}</h3>
                <p className="text-sm text-slate-600">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <CtaBand />
    </>
  );
}
